import React, { useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from '@emotion/styled'
import { Box, Button, CircularProgress, LinearProgress } from '@mui/material'
import { H3 } from '../../../layouts/TextStyles'
import { useAppSelector } from '../../../app/hooks'
import { ratingApiSlice } from '../../../features/rating/ratingSlice'
import { curatorStudentsApiSlice } from '../../../features/curator/curatorStudents'
import MonthWeekFilter from '../../../components/monthWeekFilter/MonthWeekFilter'
import CustomInput from '../../../components/muiComponents/customInput'
import FilterSelect from '../../../components/filterSelect/FilterSelect'
import RatingTable from './components/RatingTable'
import PracticeTable from './components/PracticeTable'
import ScoreScaleDialog from './components/ScoreScaleDialog'
import { DEFAULT_PAGE_SIZE } from '../../../utils/constants'
import { ui } from '../../../utils/uiTokens'
import { useInfiniteScroll } from '../../../app/hooks/useInfiniteScroll'
import { StudentRatingState } from '../../../features/types'

const RatingPage = () => {
    const { formatMessage } = useIntl()
    const { id } = useAppSelector((state) => state.user.userInfo)

    const [tab, setTab] = useState<'rating' | 'practice'>('rating')
    const [monthNumber, setMonthNumber] = useState<number | undefined>()
    const [weekNumber, setWeekNumber] = useState<number | undefined>()
    const [courseId, setCourseId] = useState<number | undefined>()
    const [groupId, setGroupId] = useState<number | undefined>()
    const [filter, setFilter] = useState('')
    const [search, setSearch] = useState('')
    const [openScale, setOpenScale] = useState(false)
    const [rows, setRows] = useState<StudentRatingState[]>([])
    const [summary, setSummary] = useState({ totalCount: 0, maxPoint: 0, averagePoint: 0 })
    const [loadedPages, setLoadedPages] = useState(0)
    // Ответы приходят не по порядку: применяем только последний запрос
    const requestIdRef = useRef(0)
    const isLoadingRef = useRef(false)

    const { data: courseResponse } = curatorStudentsApiSlice.useFetchCuratorCoursesQuery()
    const { data: groups = [] } = curatorStudentsApiSlice.useFetchCuratorGroupsQuery(courseId, {
        skip: !courseId,
    })

    const [fetchRating, { isFetching }] = ratingApiSlice.useLazyGetStudentRatingQuery()

    const hasMore = rows.length < summary.totalCount

    const loadPage = async (nextPage: number) => {
        if (nextPage > 1 && isLoadingRef.current) return

        const requestId = ++requestIdRef.current
        isLoadingRef.current = true

        try {
            const response = await fetchRating({
                groupId: groupId || 0,
                monthNumber: monthNumber || 0,
                weekNumber: weekNumber || 0,
                filter: search,
                page: nextPage - 1,
                size: DEFAULT_PAGE_SIZE,
            }).unwrap()

            if (requestId !== requestIdRef.current) return

            setRows((prev) => (nextPage === 1 ? response.data : [...prev, ...response.data]))
            setSummary({
                // Короткая страница означает конец списка: расхождение с totalCount
                // иначе гоняло бы наблюдатель по пустым страницам без остановки
                totalCount: response.data.length < DEFAULT_PAGE_SIZE
                    ? (nextPage - 1) * DEFAULT_PAGE_SIZE + response.data.length
                    : response.totalCount,
                maxPoint: response.maxPoint,
                averagePoint: response.averagePoint,
            })
            setLoadedPages(nextPage)
        } catch (error) {
            // Без остановки наблюдатель повторял бы упавший запрос бесконечно
            if (requestId === requestIdRef.current) {
                setSummary((prev) => ({ ...prev, totalCount: (nextPage - 1) * DEFAULT_PAGE_SIZE }))
            }
        } finally {
            if (requestId === requestIdRef.current) isLoadingRef.current = false
        }
    }

    const sentinelRef = useInfiniteScroll(
        () => loadPage(loadedPages + 1),
        hasMore && !isFetching && loadedPages > 0,
    )

    const handlePeriodChange = (month?: number, week?: number) => {
        setMonthNumber(month)
        setWeekNumber(week)
    }

    useEffect(() => {
        setRows([])
        setSummary({ totalCount: 0, maxPoint: 0, averagePoint: 0 })
        setLoadedPages(0)
        loadPage(1)
    }, [groupId, monthNumber, weekNumber, search])

    return (
        <Wrapper>
            <Content>
                <Header>
                    <Box>
                        <H3>{formatMessage({ id: 'rating' })}</H3>
                        <Subtitle>{formatMessage({ id: 'ratingSubtitle' })}</Subtitle>
                    </Box>

                    <Button
                        variant="outlined"
                        onClick={() => setOpenScale(true)}
                    >
                        {formatMessage({ id: 'scoreScale' })}
                    </Button>
                </Header>

                <Tabs>
                    <Tab
                        active={tab === 'rating'}
                        onClick={() => setTab('rating')}
                    >
                        {formatMessage({ id: 'ratingTable' })}
                    </Tab>
                    <Tab
                        active={tab === 'practice'}
                        onClick={() => setTab('practice')}
                    >
                        {formatMessage({ id: 'practiceAndZachet' })}
                    </Tab>
                </Tabs>

                <Filters>
                    <FilterSelect
                        label={formatMessage({ id: 'course' })}
                        placeholder={formatMessage({ id: 'course' })}
                        value={courseId}
                        options={courseResponse?.data || []}
                        onChange={(value) => {
                            setCourseId(Number(value))
                            setGroupId(undefined)
                        }}
                    />

                    <FilterSelect
                        label={formatMessage({ id: 'group' })}
                        placeholder={formatMessage({ id: 'allGroups' })}
                        value={groupId}
                        options={groups}
                        disabled={!courseId}
                        onChange={(value) => {
                            setGroupId(Number(value) || undefined)
                        }}
                    />

                    <SearchBox>
                        <CustomInput
                            margin="none"
                            size="small"
                            fullWidth
                            placeholder={formatMessage({ id: 'searchByStudentName' })}
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    setSearch(filter)
                                }
                            }}
                        />
                    </SearchBox>
                </Filters>

                <PeriodRow>
                    <MonthWeekFilter
                        monthNumber={monthNumber}
                        weekNumber={weekNumber}
                        onChange={handlePeriodChange}
                    />

                    <Summary>
                        <span>
                            {formatMessage({ id: 'maxPoint' })}: <b>{summary.maxPoint || 0}</b>
                        </span>
                        <span>
                            {formatMessage({ id: 'groupAverage' })}: <b>{summary.averagePoint || 0}</b>
                        </span>
                        <Pink>
                            {formatMessage({ id: 'studentsCount' }, { count: summary.totalCount })}
                        </Pink>
                    </Summary>
                </PeriodRow>

                {isFetching && loadedPages === 0 ? (
                    <LinearProgress />
                ) : (
                    <>
                        {tab === 'rating' ? (
                            <RatingTable
                                students={rows}
                                monthNumber={monthNumber}
                                weekNumber={weekNumber}
                            />
                        ) : (
                            <PracticeTable
                                students={rows}
                                monthNumber={monthNumber}
                                weekNumber={weekNumber}
                                groupId={groupId}
                                filter={search}
                            />
                        )}

                        <Sentinel ref={sentinelRef}>
                            {isFetching && <CircularProgress size={22} sx={{ color: ui.pink }} />}
                        </Sentinel>
                    </>
                )}
            </Content>

            <ScoreScaleDialog
                open={openScale}
                handleClose={() => setOpenScale(false)}
            />
        </Wrapper>
    )
}

export default RatingPage

const Wrapper = styled.div`
    margin: 10px;
    border-radius: 16px;
`

const Content = styled.div`
    background-color: #fff;
    border: 1px solid ${ui.border};
    border-radius: 16px;
    padding: 24px;
    min-height: 80vh;
`

const Sentinel = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 48px;
`

const Header = styled.header`
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 20px;
`

const Subtitle = styled.div`
    margin-top: 6px;
    max-width: 620px;
    font-size: 13.5px;
    line-height: 1.5;
    color: ${ui.sub};
`

const Tabs = styled.div`
    display: flex;
    gap: 8px;
    margin-bottom: 20px;
`

const Tab = styled.div<{ active: boolean }>`
    padding: 8px 16px;
    border-radius: 12px;
    cursor: pointer;
    font-size: 13.5px;
    font-weight: ${({ active }) => (active ? 600 : 400)};
    color: ${({ active }) => (active ? ui.pink : ui.sub)};
    background: ${({ active }) => (active ? ui.pinkSoft : 'transparent')};
    border: 1px solid ${({ active }) => (active ? 'transparent' : ui.border)};

    &:hover {
        border-color: ${ui.pink};
        color: ${ui.pink};
    }
`

const Filters = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
    gap: 16px;
    margin-bottom: 20px;
`

const SearchBox = styled.div`
    display: flex;
    align-items: flex-end;
    grid-column: span 2;

    @media screen and (max-width: 600px) {
        grid-column: span 1;
    }
`

const PeriodRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
`

const Summary = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    font-size: 13px;
    color: ${ui.sub};

    b {
        color: ${ui.ink};
    }
`

const Pink = styled.span`
    font-weight: 600;
    color: ${ui.pink};
`
